/**
 * Account defaults for Power Dialer answering-machine detection.
 * Pure helpers only — see amdSettings.test.js.
 */
import { AMD_MACHINE_ACTIONS, AMD_MODES } from "../calling/amd"

export {
  AMD_MACHINE_ACTIONS as AMD_ACTION_OPTIONS,
  AMD_MODES as AMD_MODE_OPTIONS,
}

/**
 * Asterisk `amd.conf` defaults — the values the modernised ViciDial runs.
 * Kept in step with crm/amd.py ASTERISK_REFERENCE_PROFILE.
 */
export const DEFAULT_AMD_CONFIG = {
  profile: "asterisk_reference",
  total_analysis_time: 5000,
  initial_silence: 2500,
  greeting: 1500,
  after_greeting_silence: 800,
  silence_threshold: 256,
  min_word_length: 100,
  maximum_word_length: 5000,
  between_words_silence: 50,
  maximum_number_of_words: 2,
  message_end_timeout: 30000,
  ring_timeout: 30,
  voicemail_lead_in_ms: 1000,
  text_quiet_hours: { enabled: true, start_hour: 21, end_hour: 8 },
}

export const TWILIO_DEFAULT_AMD_CONFIG = {
  ...DEFAULT_AMD_CONFIG,
  profile: "twilio_default",
  total_analysis_time: 30000,
  initial_silence: 5000,
  greeting: 2400,
  after_greeting_silence: 1200,
}

/** Knobs the hosted Twilio detector actually uses. */
export const TUNING_FIELDS = [
  {
    key: "greeting",
    label: "Speech longer than this reads as a machine (ms)",
    min: 250,
    max: 10000,
  },
  {
    key: "after_greeting_silence",
    label: "Silence after speech before deciding (ms)",
    min: 100,
    max: 10000,
  },
  {
    key: "initial_silence",
    label: "Silence before any greeting (ms)",
    min: 500,
    max: 20000,
  },
  {
    key: "total_analysis_time",
    label: "Give up and connect after (ms)",
    min: 1000,
    max: 60000,
  },
  {
    key: "message_end_timeout",
    label: "Max wait for the beep (ms)",
    min: 3000,
    max: 59000,
  },
  { key: "ring_timeout", label: "Ring timeout (seconds)", min: 5, max: 120 },
  {
    key: "voicemail_lead_in_ms",
    label: "Pause before playing the voicemail (ms)",
    min: 0,
    max: 5000,
  },
]

/**
 * Carried for the native detector we can run on Media Streams later; the
 * hosted Twilio detector has no equivalent for these.
 */
export const ADVANCED_FIELDS = [
  {
    key: "silence_threshold",
    label: "Silence energy threshold",
    min: 1,
    max: 32767,
  },
  {
    key: "min_word_length",
    label: "Shortest counted word (ms)",
    min: 10,
    max: 5000,
  },
  {
    key: "maximum_word_length",
    label: "Longest single utterance (ms)",
    min: 100,
    max: 30000,
  },
  {
    key: "between_words_silence",
    label: "Gap that separates words (ms)",
    min: 10,
    max: 2000,
  },
  {
    key: "maximum_number_of_words",
    label: "More words than this reads as a machine",
    min: 1,
    max: 20,
  },
]

const NUMERIC_KEYS = [...TUNING_FIELDS, ...ADVANCED_FIELDS].map(
  (field) => field.key
)

function clampHour(value) {
  const parsed = Number.parseInt(value, 10)
  if (Number.isNaN(parsed)) return 0
  return Math.max(0, Math.min(23, parsed))
}

/** Coerce user input to integers, dropping anything unparseable. */
export function normalizeAmdConfigInput(config) {
  const base = {
    ...DEFAULT_AMD_CONFIG,
    text_quiet_hours: { ...DEFAULT_AMD_CONFIG.text_quiet_hours },
  }
  if (!config || typeof config !== "object") {
    return base
  }
  if (config.profile === "twilio_default") {
    Object.assign(base, TWILIO_DEFAULT_AMD_CONFIG, {
      text_quiet_hours: { ...base.text_quiet_hours },
    })
  }
  NUMERIC_KEYS.forEach((key) => {
    if (
      config[key] === undefined ||
      config[key] === null ||
      config[key] === ""
    ) {
      return
    }
    const parsed = Number.parseInt(config[key], 10)
    if (!Number.isNaN(parsed)) {
      base[key] = parsed
    }
  })
  const quiet = config.text_quiet_hours
  if (quiet && typeof quiet === "object") {
    base.text_quiet_hours = {
      enabled: Boolean(quiet.enabled),
      start_hour: clampHour(quiet.start_hour ?? 21),
      end_hour: clampHour(quiet.end_hour ?? 8),
    }
  }
  return base
}

/** Form state from the CRM settings object held in redux. */
export function amdFormFromSettings(settings) {
  return {
    amd_enabled: Boolean(settings?.amd_enabled),
    amd_machine_action: settings?.amd_machine_action || "hangup",
    amd_mode: settings?.amd_mode || "listen_in",
    sms_campaign_id: settings?.amd_sms_campaign ?? null,
    voicemail_recording_id: settings?.amd_voicemail_recording ?? null,
    amd_config: normalizeAmdConfigInput(settings?.amd_config),
  }
}

function configsDiffer(a, b) {
  return JSON.stringify(a) !== JSON.stringify(b)
}

/** Only the fields that actually changed, shaped for PATCH /crm/api/offer-settings/. */
export function buildAmdSettingsPatch(original, form) {
  const current = amdFormFromSettings(original)
  const patch = {}
  if (form.amd_enabled !== current.amd_enabled) {
    patch.amd_enabled = form.amd_enabled
  }
  if (form.amd_machine_action !== current.amd_machine_action) {
    patch.amd_machine_action = form.amd_machine_action
  }
  if (form.amd_mode !== current.amd_mode) {
    patch.amd_mode = form.amd_mode
  }
  if ((form.sms_campaign_id ?? null) !== (current.sms_campaign_id ?? null)) {
    patch.amd_sms_campaign_id = form.sms_campaign_id ?? null
  }
  if (
    (form.voicemail_recording_id ?? null) !==
    (current.voicemail_recording_id ?? null)
  ) {
    patch.amd_voicemail_recording_id = form.voicemail_recording_id ?? null
  }
  const nextConfig = normalizeAmdConfigInput(form.amd_config)
  if (configsDiffer(nextConfig, current.amd_config)) {
    patch.amd_config = nextConfig
  }
  return patch
}

export function isAmdFormDirty(original, form) {
  return Object.keys(buildAmdSettingsPatch(original, form)).length > 0
}

/** Why the account defaults cannot be saved as chosen (or null). */
export function amdSettingsBlocker(form, { smsReady, recordingCount } = {}) {
  if (!form?.amd_enabled) return null
  if (form.amd_machine_action === "text") {
    if (!form.sms_campaign_id) {
      return "Choose the text to send when voicemail picks up."
    }
    if (smsReady === false) {
      return "Finish SMS setup above before the dialer can text leads."
    }
  }
  if (form.amd_machine_action === "voicemail") {
    if (!form.voicemail_recording_id) {
      return "Choose the voicemail recording to play."
    }
    if (recordingCount === 0) {
      return "Record or upload a voicemail below first."
    }
  }
  return null
}

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label: `${String(hour).padStart(2, "0")}:00`,
}))
